import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Tooltip, Slider, Divider, useMediaQuery, useTheme, Zoom, InputAdornment,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon, Edit, Delete, ShowChart, AccountBalance,
  TrendingUp, CalendarToday, Info as InfoIcon, Save, Euro
} from '@mui/icons-material';
import { useIntl, FormattedMessage } from 'react-intl';
import { ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { User } from 'firebase/auth';
import { Investment } from '../types/Investment';
import { getInvestments, addInvestment, updateInvestment, deleteInvestment } from '../firebase/firestoreOperations';
import { getMultipleCryptoPrices } from '../services/coingeckoService';
import { designTokens } from '../theme/designTokens';

interface BenjaminEtfsProps {
  user: User;
}

const COLORS = ['#3b82f6', '#10b981']; // S&P 500 (Blue), Nasdaq (Green)

export const BenjaminEtfs: React.FC<BenjaminEtfsProps> = ({ user }) => {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Database Investments
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal forms
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState<Investment | null>(null);

  // Form Fields
  const [etfToken, setEtfToken] = useState('SXR8'); // SXR8 = S&P500, SXRV = Nasdaq
  const [amount, setAmount] = useState('');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');

  // Editable current prices (default estimates, saved in localStorage)
  const [currentPriceSXR8, setCurrentPriceSXR8] = useState<number>(() => {
    const saved = localStorage.getItem('etf_price_SXR8');
    return saved ? parseFloat(saved) : 540;
  });
  const [currentPriceSXRV, setCurrentPriceSXRV] = useState<number>(() => {
    const saved = localStorage.getItem('etf_price_SXRV');
    return saved ? parseFloat(saved) : 1050;
  });
  const [isEditingPrices, setIsEditingPrices] = useState(false);

  // Simulator Sliders
  const [simMonthly, setSimMonthly] = useState<number>(100);
  const [simReturn, setSimReturn] = useState<number>(8);
  const [simTargetAge, setSimTargetAge] = useState<number>(60);
  const CURRENT_AGE = 11;

  const fetchEtfInvestments = async () => {
    if (user) {
      try {
        setIsLoading(true);
        const all = await getInvestments(user.uid);
        // Filter only Benjamin's ETFs
        const etfs = all.filter(inv => inv.type === 'etf' && inv.owner === 'benjamin');
        setInvestments(etfs);

        // Attempt to fetch current prices from CoinGecko in case they are tracked
        try {
          const prices = await getMultipleCryptoPrices(['SXR8', 'SXRV']);
          if (prices['SXR8']?.price) {
            setCurrentPriceSXR8(prices['SXR8'].price);
            localStorage.setItem('etf_price_SXR8', prices['SXR8'].price.toString());
          }
          if (prices['SXRV']?.price) {
            setCurrentPriceSXRV(prices['SXRV'].price);
            localStorage.setItem('etf_price_SXRV', prices['SXRV'].price.toString());
          }
        } catch (e) {
          console.log('CoinGecko ETF price fetch not found or skipped. Using manual fallbacks.');
        }
      } catch (error) {
        console.error('Error fetching ETF investments:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchEtfInvestments();
  }, [user]);

  // Save manual prices
  const handleSavePrices = () => {
    localStorage.setItem('etf_price_SXR8', currentPriceSXR8.toString());
    localStorage.setItem('etf_price_SXRV', currentPriceSXRV.toString());
    setIsEditingPrices(false);
  };

  // ETF name helper
  const getEtfName = (token: string) => {
    return token === 'SXR8' ? 'iShares S&P 500 (SXR8)' : 'iShares Nasdaq 100 (SXRV)';
  };

  // Portfolio calculations
  const portfolioStats = useMemo(() => {
    let totalInvested = 0;
    let totalSXR8Invested = 0;
    let totalSXR8Qty = 0;
    let totalSXRVInvested = 0;
    let totalSXRVQty = 0;

    investments.forEach(inv => {
      const invValue = parseFloat(inv.investment);
      totalInvested += invValue;

      if (inv.token === 'SXR8') {
        totalSXR8Invested += invValue;
        totalSXR8Qty += Number(inv.quantity);
      } else if (inv.token === 'SXRV') {
        totalSXRVInvested += invValue;
        totalSXRVQty += Number(inv.quantity);
      }
    });

    const currentSXR8Value = totalSXR8Qty * currentPriceSXR8;
    const currentSXRVValue = totalSXRVQty * currentPriceSXRV;
    const totalCurrentValue = currentSXR8Value + currentSXRVValue;

    const profitLoss = totalCurrentValue - totalInvested;
    const profitLossPercent = totalInvested > 0 ? (profitLoss / totalInvested) * 100 : 0;

    const sxr8AvgPrice = totalSXR8Qty > 0 ? totalSXR8Invested / totalSXR8Qty : 0;
    const sxrvAvgPrice = totalSXRVQty > 0 ? totalSXRVInvested / totalSXRVQty : 0;

    const sxr8ProfitLoss = currentSXR8Value - totalSXR8Invested;
    const sxr8ProfitPercent = totalSXR8Invested > 0 ? (sxr8ProfitLoss / totalSXR8Invested) * 100 : 0;

    const sxrvProfitLoss = currentSXRVValue - totalSXRVInvested;
    const sxrvProfitPercent = totalSXRVInvested > 0 ? (sxrvProfitLoss / totalSXRVInvested) * 100 : 0;

    return {
      totalInvested,
      totalCurrentValue,
      profitLoss,
      profitLossPercent,
      SXR8: {
        invested: totalSXR8Invested,
        qty: totalSXR8Qty,
        avgPrice: sxr8AvgPrice,
        currentValue: currentSXR8Value,
        profitLoss: sxr8ProfitLoss,
        profitPercent: sxr8ProfitPercent
      },
      SXRV: {
        invested: totalSXRVInvested,
        qty: totalSXRVQty,
        avgPrice: sxrvAvgPrice,
        currentValue: currentSXRVValue,
        profitLoss: sxrvProfitLoss,
        profitPercent: sxrvProfitPercent
      }
    };
  }, [investments, currentPriceSXR8, currentPriceSXRV]);

  // Allocation Chart Data
  const allocationData = useMemo(() => {
    return [
      { name: 'S&P 500 (SXR8)', value: portfolioStats.SXR8.currentValue || 0 },
      { name: 'Nasdaq 100 (SXRV)', value: portfolioStats.SXRV.currentValue || 0 }
    ].filter(item => item.value > 0);
  }, [portfolioStats]);

  // Reinforcements Trend Data (Timeline)
  const timelineData = useMemo(() => {
    const sorted = [...investments].sort((a, b) => {
      const da = a.purchaseDate ? new Date(a.purchaseDate).getTime() : 0;
      const db = b.purchaseDate ? new Date(b.purchaseDate).getTime() : 0;
      return da - db;
    });

    let runningTotal = 0;
    let runningSXR8Total = 0;
    let runningSXRVTotal = 0;

    return sorted.map(inv => {
      const amount = parseFloat(inv.investment);
      runningTotal += amount;
      if (inv.token === 'SXR8') runningSXR8Total += amount;
      else if (inv.token === 'SXRV') runningSXRVTotal += amount;

      const dateStr = inv.purchaseDate
        ? new Date(inv.purchaseDate).toLocaleDateString(intl.locale === 'pt-PT' ? 'pt-PT' : 'en-US', { month: 'short', year: '2-digit' })
        : '';

      return {
        date: dateStr,
        Total: runningTotal,
        'S&P 500': runningSXR8Total,
        Nasdaq: runningSXRVTotal
      };
    });
  }, [investments, intl.locale]);

  // Savings Projection Data
  const projectionData = useMemo(() => {
    const data = [];
    const monthlyRate = (simReturn / 100) / 12;
    let balance = portfolioStats.totalCurrentValue;
    let totalInvested = portfolioStats.totalInvested;

    data.push({
      age: CURRENT_AGE,
      [intl.formatMessage({ id: 'benjaminEtfs.totalInvested' })]: Math.round(totalInvested),
      [intl.formatMessage({ id: 'benjaminEtfs.projectedValue' })]: Math.round(balance)
    });

    const totalMonths = Math.max(0, (simTargetAge - CURRENT_AGE) * 12);

    for (let month = 1; month <= totalMonths; month++) {
      balance = (balance + simMonthly) * (1 + monthlyRate);
      totalInvested += simMonthly;

      if (month % 12 === 0) {
        data.push({
          age: CURRENT_AGE + (month / 12),
          [intl.formatMessage({ id: 'benjaminEtfs.totalInvested' })]: Math.round(totalInvested),
          [intl.formatMessage({ id: 'benjaminEtfs.projectedValue' })]: Math.round(balance)
        });
      }
    }
    return data;
  }, [portfolioStats.totalCurrentValue, portfolioStats.totalInvested, simMonthly, simReturn, simTargetAge, intl]);

  // Form Management
  const handleOpenAdd = () => {
    setEditingInvestment(null);
    setEtfToken('SXR8');
    setAmount('');
    setQuantity('');
    setPrice('');
    setDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setIsFormOpen(true);
  };

  const handleOpenEdit = (inv: Investment) => {
    setEditingInvestment(inv);
    setEtfToken(inv.token);
    setAmount(inv.investment);
    setQuantity(inv.quantity.toString());
    setPrice(inv.purchasePrice.toString());
    setDate(inv.purchaseDate ? new Date(inv.purchaseDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
    setNotes(inv.notes || '');
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(intl.formatMessage({ id: 'benjaminEtfs.confirmDelete' }))) {
      try {
        await deleteInvestment(id, user.uid);
        fetchEtfInvestments();
      } catch (error) {
        console.error('Error deleting reinforcement:', error);
      }
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !quantity || !price || !date) return;

    const parsedAmount = parseFloat(amount);
    const parsedQty = parseFloat(quantity);
    const parsedPrice = parseFloat(price);
    const purchaseDate = new Date(date);

    const dataPayload: Omit<Investment, 'id'> = {
      userId: user.uid,
      token: etfToken,
      investment: parsedAmount.toString(),
      quantity: parsedQty,
      purchasePrice: parsedPrice,
      purchaseDate,
      type: 'etf',
      owner: 'benjamin',
      notes
    };

    try {
      if (editingInvestment) {
        await updateInvestment(editingInvestment.id, dataPayload, user.uid);
      } else {
        await addInvestment(user.uid, dataPayload);
      }
      setIsFormOpen(false);
      fetchEtfInvestments();
    } catch (error) {
      console.error('Error saving reinforcement:', error);
    }
  };

  // Autocalculate Price/Quantity relationship
  const handleAmountChange = (val: string) => {
    setAmount(val);
    const a = parseFloat(val);
    const p = parseFloat(price);
    if (!isNaN(a) && !isNaN(p) && p > 0) {
      setQuantity((a / p).toFixed(6));
    }
  };

  const handlePriceChange = (val: string) => {
    setPrice(val);
    const p = parseFloat(val);
    const a = parseFloat(amount);
    if (!isNaN(p) && p > 0 && !isNaN(a)) {
      setQuantity((a / p).toFixed(6));
    }
  };

  const handleQuantityChange = (val: string) => {
    setQuantity(val);
    const q = parseFloat(val);
    const p = parseFloat(price);
    if (!isNaN(q) && !isNaN(p)) {
      setAmount((q * p).toFixed(2));
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
      
      {/* Top Banner / Header */}
      <Box sx={{
        p: 3,
        borderRadius: designTokens.borderRadius.lg,
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        boxShadow: designTokens.shadows.md,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            <FormattedMessage id="benjaminEtfs.title" />
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85 }}>
            <FormattedMessage id="benjaminEtfs.subtitle" />
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
          sx={{
            bgcolor: 'white',
            color: '#1e3a8a',
            fontWeight: 'bold',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.9)'
            }
          }}
        >
          <FormattedMessage id="benjaminEtfs.addReinforcement" />
        </Button>
      </Box>

      {/* Main Stats Row */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: designTokens.colors.background.paper, border: designTokens.glassmorphism.border, boxShadow: designTokens.shadows.md }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  <FormattedMessage id="benjaminEtfs.totalInvested" />
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', my: 1 }}>
                  € {portfolioStats.totalInvested.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Valor total dos reforços na XTB
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', width: 56, height: 56 }}>
                <AccountBalance />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ background: designTokens.colors.background.paper, border: designTokens.glassmorphism.border, boxShadow: designTokens.shadows.md }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Valor Atual Estimado
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', my: 1 }}>
                  € {portfolioStats.totalCurrentValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Baseado nos preços atuais configurados
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', width: 56, height: 56 }}>
                <ShowChart />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ background: designTokens.colors.background.paper, border: designTokens.glassmorphism.border, boxShadow: designTokens.shadows.md }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Lucro / Prejuízo Total
                </Typography>
                <Typography variant="h4" sx={{
                  fontWeight: 'bold',
                  my: 1,
                  color: portfolioStats.profitLoss >= 0 ? 'success.main' : 'error.main'
                }}>
                  € {portfolioStats.profitLoss >= 0 ? '+' : ''}{portfolioStats.profitLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography variant="body2" sx={{
                  fontWeight: 'semibold',
                  color: portfolioStats.profitLoss >= 0 ? 'success.main' : 'error.main'
                }}>
                  {portfolioStats.profitLoss >= 0 ? '▲' : '▼'} {portfolioStats.profitLossPercent.toFixed(2)}%
                </Typography>
              </Box>
              <Avatar sx={{
                bgcolor: portfolioStats.profitLoss >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                color: portfolioStats.profitLoss >= 0 ? '#10b981' : '#ef4444',
                width: 56,
                height: 56
              }}>
                <TrendingUp />
              </Avatar>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ETF Pricing Settings & Split Row */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ height: '100%', border: designTokens.glassmorphism.border, boxShadow: designTokens.shadows.md }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Posições Detalhadas dos ETFs
                </Typography>
                {!isEditingPrices ? (
                  <Button variant="outlined" size="small" onClick={() => setIsEditingPrices(true)}>
                    Ajustar Preços Atuais
                  </Button>
                ) : (
                  <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSavePrices}>
                    Guardar Preços
                  </Button>
                )}
              </Box>

              {isEditingPrices && (
                <Grid container spacing={2} sx={{ mb: 3, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Preço Atual do S&P 500 (SXR8) (€)"
                      type="number"
                      size="small"
                      value={currentPriceSXR8}
                      onChange={(e) => setCurrentPriceSXR8(parseFloat(e.target.value) || 0)}
                      InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Preço Atual do Nasdaq 100 (SXRV) (€)"
                      type="number"
                      size="small"
                      value={currentPriceSXRV}
                      onChange={(e) => setCurrentPriceSXRV(parseFloat(e.target.value) || 0)}
                      InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                    />
                  </Grid>
                </Grid>
              )}

              <Grid container spacing={2}>
                {/* S&P 500 Block */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    borderLeft: '4px solid #3b82f6',
                    bgcolor: 'rgba(59, 130, 246, 0.03)',
                    height: '100%'
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#3b82f6' }}>
                      S&P 500 (SXR8)
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Total Investido:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>€ {portfolioStats.SXR8.invested.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Total Unidades:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{portfolioStats.SXR8.qty.toFixed(4)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Preço Médio:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>€ {portfolioStats.SXR8.avgPrice.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Preço Atual:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>€ {currentPriceSXR8.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Lucro/Prejuízo:</Typography>
                        <Typography variant="body2" sx={{
                          fontWeight: 'bold',
                          color: portfolioStats.SXR8.profitLoss >= 0 ? 'success.main' : 'error.main'
                        }}>
                          € {portfolioStats.SXR8.profitLoss >= 0 ? '+' : ''}{portfolioStats.SXR8.profitLoss.toFixed(2)} ({portfolioStats.SXR8.profitPercent.toFixed(2)}%)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* Nasdaq Block */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    borderLeft: '4px solid #10b981',
                    bgcolor: 'rgba(16, 185, 129, 0.03)',
                    height: '100%'
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                      Nasdaq 100 (SXRV)
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Total Investido:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>€ {portfolioStats.SXRV.invested.toLocaleString()}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Total Unidades:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{portfolioStats.SXRV.qty.toFixed(4)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Preço Médio:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>€ {portfolioStats.SXRV.avgPrice.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Preço Atual:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>€ {currentPriceSXRV.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Lucro/Prejuízo:</Typography>
                        <Typography variant="body2" sx={{
                          fontWeight: 'bold',
                          color: portfolioStats.SXRV.profitLoss >= 0 ? 'success.main' : 'error.main'
                        }}>
                          € {portfolioStats.SXRV.profitLoss >= 0 ? '+' : ''}{portfolioStats.SXRV.profitLoss.toFixed(2)} ({portfolioStats.SXRV.profitPercent.toFixed(2)}%)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Allocation Pie Chart */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%', border: designTokens.glassmorphism.border, boxShadow: designTokens.shadows.md }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 300 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                <FormattedMessage id="benjaminEtfs.allocation" />
              </Typography>
              {allocationData.length > 0 ? (
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={allocationData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {allocationData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[entry.name.includes('SXR8') ? 0 : 1]} />
                        ))}
                      </Pie>
                      <ChartTooltip formatter={(value: number) => `€ ${value.toLocaleString()}`} />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Sem dados para exibir</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Accumulated Contributions Chart */}
      {timelineData.length > 0 && (
        <Card sx={{ border: designTokens.glassmorphism.border, boxShadow: designTokens.shadows.md }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Evolução das Contribuições Acumuladas
            </Typography>
            <Box sx={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timelineData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="date" stroke={theme.palette.text.secondary} fontSize={12} />
                  <YAxis stroke={theme.palette.text.secondary} fontSize={12} tickFormatter={(v) => `€ ${v}`} />
                  <ChartTooltip formatter={(value: number) => `€ ${value.toLocaleString()}`} />
                  <Legend />
                  <Area type="monotone" dataKey="Total" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorTotal)" />
                  <Area type="monotone" dataKey="S&P 500" stroke="#60a5fa" strokeWidth={1} fillOpacity={0} />
                  <Area type="monotone" dataKey="Nasdaq" stroke="#34d399" strokeWidth={1} fillOpacity={0} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* History of Reinforcements */}
      <Card sx={{ border: designTokens.glassmorphism.border, boxShadow: designTokens.shadows.md }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            <FormattedMessage id="benjaminEtfs.history" />
          </Typography>
          {investments.length > 0 ? (
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><FormattedMessage id="benjaminEtfs.date" /></TableCell>
                    <TableCell><FormattedMessage id="benjaminEtfs.etf" /></TableCell>
                    <TableCell align="right"><FormattedMessage id="benjaminEtfs.amount" /></TableCell>
                    <TableCell align="right"><FormattedMessage id="benjaminEtfs.quantity" /></TableCell>
                    <TableCell align="right"><FormattedMessage id="benjaminEtfs.pricePerUnit" /></TableCell>
                    <TableCell><FormattedMessage id="benjaminEtfs.notes" /></TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...investments].sort((a, b) => {
                    const da = a.purchaseDate ? new Date(a.purchaseDate).getTime() : 0;
                    const db = b.purchaseDate ? new Date(b.purchaseDate).getTime() : 0;
                    return db - da; // Show latest first in the table
                  }).map((inv) => (
                    <TableRow key={inv.id} hover>
                      <TableCell>
                        {inv.purchaseDate ? new Date(inv.purchaseDate).toLocaleDateString() : ''}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'semibold', color: inv.token === 'SXR8' ? '#3b82f6' : '#10b981' }}>
                        {inv.token === 'SXR8' ? 'S&P 500' : 'Nasdaq 100'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'semibold' }}>
                        € {parseFloat(inv.investment).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="right">
                        {inv.quantity.toFixed(4)}
                      </TableCell>
                      <TableCell align="right">
                        € {inv.purchasePrice.toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {inv.notes || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => handleOpenEdit(inv)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(inv.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">Nenhum reforço registado até ao momento.</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ mt: 2 }}>
                Registar o primeiro reforço
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Simulator Section */}
      <Card sx={{ border: designTokens.glassmorphism.border, boxShadow: designTokens.shadows.md }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              <FormattedMessage id="benjaminEtfs.projectionTitle" />
            </Typography>
            <Tooltip title="Projeção utilizando fórmula de juros compostos com reforços mensais capitalizados mensalmente.">
              <IconButton size="small"><InfoIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            <FormattedMessage id="benjaminEtfs.projectionSubtitle" />
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Sliders */}
              <Box>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  <FormattedMessage id="benjaminEtfs.monthlyTarget" />: € {simMonthly}
                </Typography>
                <Slider
                  value={simMonthly}
                  onChange={(_, val) => setSimMonthly(val as number)}
                  min={10}
                  max={1000}
                  step={10}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  <FormattedMessage id="benjaminEtfs.expectedReturn" />: {simReturn}% ao ano
                </Typography>
                <Slider
                  value={simReturn}
                  onChange={(_, val) => setSimReturn(val as number)}
                  min={1}
                  max={15}
                  step={0.5}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                  <FormattedMessage id="benjaminEtfs.targetAge" />: {simTargetAge} anos
                </Typography>
                <Slider
                  value={simTargetAge}
                  onChange={(_, val) => setSimTargetAge(val as number)}
                  min={12}
                  max={75}
                  step={1}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: 'rgba(59, 130, 246, 0.05)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                mt: 1
              }}>
                <Typography variant="subtitle2" color="text.secondary">Valor Final Estimado (aos {simTargetAge} anos):</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', my: 1 }}>
                  € {projectionData[projectionData.length - 1]?.[intl.formatMessage({ id: 'benjaminEtfs.projectedValue' })]?.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Das quais € {projectionData[projectionData.length - 1]?.[intl.formatMessage({ id: 'benjaminEtfs.totalInvested' })]?.toLocaleString()} são contribuições diretas.
                </Typography>
              </Box>
            </Grid>

            {/* Projection Chart */}
            <Grid item xs={12} md={7}>
              <Box sx={{ width: '100%', height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={projectionData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorProjected" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis dataKey="age" stroke={theme.palette.text.secondary} fontSize={12} tickFormatter={(v) => `${v} anos`} />
                    <YAxis stroke={theme.palette.text.secondary} fontSize={12} tickFormatter={(v) => `€ ${v}`} />
                    <ChartTooltip formatter={(value: number) => `€ ${value.toLocaleString()}`} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey={intl.formatMessage({ id: 'benjaminEtfs.projectedValue' })}
                      stroke="#10b981"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorProjected)"
                    />
                    <Area
                      type="monotone"
                      dataKey={intl.formatMessage({ id: 'benjaminEtfs.totalInvested' })}
                      stroke="#3b82f6"
                      strokeWidth={2}
                      fillOpacity={0}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Add/Edit Modal Form */}
      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSave}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>
            {editingInvestment ? (
              <FormattedMessage id="benjaminEtfs.editReinforcement" />
            ) : (
              <FormattedMessage id="benjaminEtfs.addReinforcement" />
            )}
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              select
              fullWidth
              label={<FormattedMessage id="benjaminEtfs.etf" />}
              value={etfToken}
              onChange={(e) => setEtfToken(e.target.value)}
              margin="dense"
            >
              <MenuItem value="SXR8">S&P 500 (SXR8.DE)</MenuItem>
              <MenuItem value="SXRV">Nasdaq 100 (SXRV.DE)</MenuItem>
            </TextField>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={<FormattedMessage id="benjaminEtfs.amount" />}
                  type="number"
                  inputProps={{ step: '0.01' }}
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={<FormattedMessage id="benjaminEtfs.price" />}
                  type="number"
                  inputProps={{ step: '0.01' }}
                  value={price}
                  onChange={(e) => handlePriceChange(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                  required
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label={<FormattedMessage id="benjaminEtfs.quantity" />}
              type="number"
              inputProps={{ step: '0.000001' }}
              value={quantity}
              onChange={(e) => handleQuantityChange(e.target.value)}
              helperText="Calculado automaticamente a partir do valor e preço unitário"
              required
            />

            <TextField
              fullWidth
              label={<FormattedMessage id="benjaminEtfs.date" />}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              fullWidth
              label={<FormattedMessage id="benjaminEtfs.notes" />}
              multiline
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="ex: Oferta de Natal do Avô"
            />
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setIsFormOpen(false)}>Cancelar</Button>
            <Button variant="contained" type="submit">
              {editingInvestment ? 'Guardar' : 'Adicionar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};
